import React, { useCallback, useEffect, useState } from "react";
import Header from "../../components/Header";
import AddButton from "../../components/AddButton";
import DeleteModal from "../../components/DeleteModal";
import Pagination from "../../components/Pagination";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router";
import { getCombinedWorkouts } from "../../services/programService";
import {
  removeWorkoutCategory,
  removeWorkoutProgram,
} from "../../redux/slices/programSlice";
import { Program } from "../../types/program";
import Loading from "../../components/Loading";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";

const Programs: React.FC = () => {
  // const [programs, setPrograms] = useState<Program[]>([]);
  // const [categories, setCategories] = useState<Program[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Program | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [data, setData] = useState<Program[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getCombinedWorkouts(currentPage, 10);
        const combined = [
          ...response.categories.map((item: any) => ({
            ...item,
            type: "Category",
          })),
          ...response.programs.map((item: any) => ({
            ...item,
            type: "Program",
          })),
        ];
        setData(combined);
        setTotalPages(response.totalPages);
      } catch (error) {
        setError("Failed to fetch data. Please try again.");
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  console.log("Data:", data);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const openDeleteModal = (item: Program) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCombinedWorkouts(currentPage, 10);
      const combined = [
        ...response.categories.map((item: any) => ({
          ...item,
          type: "Category",
        })),
        ...response.programs.map((item: any) => ({ ...item, type: "Program" })),
      ];
      setData(combined);
      setTotalPages(response.totalPages);
    } catch (error) {
      setError("Failed to fetch data. Please try again.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      try {
        if (itemToDelete.type === "Program") {
          await dispatch(removeWorkoutProgram(itemToDelete.id)).unwrap();
        } else if (itemToDelete.type === "Category") {
          await dispatch(removeWorkoutCategory(itemToDelete.id)).unwrap();
        }

        setItemToDelete(null);
        setIsDeleteModalOpen(false);
        setSuccessMessage(`${itemToDelete.type} deleted successfully!`);
        fetchData();
      } catch (error) {
        console.error("Error deleting item:", error);
        setError("Failed to delete item. Please try again.");
      }
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Programs & Categories" />
      <div className="mt-4">
        {loading ? (
          <Loading message="Loading programs and categories..." />
        ) : (
          <>
            {successMessage && (
              <SuccessMessage
                message={successMessage}
                onClose={() => setSuccessMessage(null)}
              />
            )}
            {error && (
              <ErrorMessage message={error} onClose={() => setError(null)} />
            )}
            <div className="mb-4 flex justify-end">
              <AddButton
                text="Add Program"
                onClick={() => navigate("/add-program")}
              />
            </div>
            <div className="rounded-lg overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="py-2 px-4 text-left">ID</th>
                    <th className="py-2 px-4 text-left">Name</th>
                    <th className="py-2 px-4 text-left">Type</th>
                    <th className="py-2 px-4 text-left">Image</th>
                    <th className="py-2 px-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => (
                    <tr
                      key={`${item.type}-${item.id}`}
                      className="border-t hover:bg-gray-50"
                    >
                      <td className="py-3 px-4 text-sm">{item.id}</td>
                      <td className="py-3 px-4 text-sm">{item.title}</td>
                      <td className="py-3 px-4 text-sm">{item.type}</td>
                      <td className="py-3 px-4 text-sm">
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              navigate(`/edit-program`, { state: item })
                            }
                            className="px-2 py-1 text-yellow-600 border border-yellow-600 rounded hover:bg-gray-100"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(item)}
                            className="px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        itemName={itemToDelete?.title || ""}
        itemType="item"
      />
    </div>
  );
};

export default Programs;
